import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translateWithVars } from '../utils/i18n/translateWithVars';

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  showPageSizeSelector = true,
  pageSizeOptions = [25, 50, 100, 200, 500],
  onPageSizeChange
}) => {
  const { t, isRTL } = useLanguage();

  const safeCurrentPage = Number(currentPage) || 1;
  const safeItemsPerPage = Number(itemsPerPage) || 50;
  const safeTotalItems = Number(totalItems) || 0;
  const safeTotalPages = Number(totalPages) || 1;

  const startItem = (safeCurrentPage - 1) * safeItemsPerPage + 1;
  const endItem = Math.min(safeCurrentPage * safeItemsPerPage, safeTotalItems);

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, safeCurrentPage - delta); i <= Math.min(safeTotalPages - 1, safeCurrentPage + delta); i++) {
      range.push(i);
    }

    if (safeCurrentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (safeCurrentPage + delta < safeTotalPages - 1) {
      rangeWithDots.push('...', safeTotalPages);
    } else if (safeTotalPages > 1) {
      rangeWithDots.push(safeTotalPages);
    }

    return rangeWithDots;
  };

  if (safeTotalPages <= 1) return null;

  const pageOfText = translateWithVars(t('paginationPage.pageOf'), {
    current: safeCurrentPage,
    total: safeTotalPages
  });

  const showingText = translateWithVars(t('paginationPage.showing'), {
    start: startItem,
    end: endItem,
    total: safeTotalItems
  });

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between flex-1 sm:hidden">
        <button
          onClick={() => onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 1}
          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('paginationPage.previous')}
        </button>
        <span className="text-sm text-gray-700">{pageOfText}</span>
        <button
          onClick={() => onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage === safeTotalPages}
          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('paginationPage.next')}
        </button>
      </div>

      <div className="hidden sm:flex sm:items-center sm:justify-between sm:flex-1">
        <div className="flex items-center space-x-4">
          <p className="text-sm text-gray-700">{showingText}</p>

          {showPageSizeSelector && onPageSizeChange && (
            <div className="flex items-center space-x-2">
              <label htmlFor="page-size" className="text-sm text-gray-700">
                {t('paginationPage.showLabel')}
              </label>
              <select
                id="page-size"
                value={itemsPerPage}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="block w-20 px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-1">
          <button
            onClick={() => onPageChange(1)}
            disabled={safeCurrentPage === 1}
            className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('paginationPage.firstPage')}
          >
            <ChevronsLeft className="w-4 h-4 ads-icon-dir" />
          </button>

          <button
            onClick={() => onPageChange(safeCurrentPage - 1)}
            disabled={safeCurrentPage === 1}
            className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('paginationPage.previousPage')}
          >
            <ChevronLeft className="w-4 h-4 ads-icon-dir" />
          </button>

          {getVisiblePages().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' && onPageChange(page)}
              disabled={page === '...'}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${
                page === safeCurrentPage
                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 cursor-pointer'
                  : page === '...'
                  ? 'text-gray-500 cursor-default'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 cursor-pointer'
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => onPageChange(safeCurrentPage + 1)}
            disabled={safeCurrentPage === safeTotalPages}
            className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('paginationPage.nextPage')}
          >
            <ChevronRight className="w-4 h-4 ads-icon-dir" />
          </button>

          <button
            onClick={() => onPageChange(safeTotalPages)}
            disabled={safeCurrentPage === safeTotalPages}
            className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('paginationPage.lastPage')}
          >
            <ChevronsRight className="w-4 h-4 ads-icon-dir" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
